import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Siswa, TagihanSiswa, PaymentChannel, TransaksiPembayaran } from '../../types/index.js';
import { QrisPaymentModal } from './QrisPaymentModal.js';
import { BarcodeScannerModal } from './BarcodeScannerModal.js';
import { VAPaymentModal, BankSelectorModal, VA_BANK_LIST } from './VAPaymentModal.js';
import { useDialog } from '../ui/DialogProvider.js';
import { api } from '../../services/api.js';
import { SearchableSelect } from '../ui/SearchableSelect.js';
import {
  Search,
  CreditCard,
  Wallet,
  Building2,
  CheckCircle2,
  AlertCircle,
  Printer,
  Sparkles,
  QrCode,
  Banknote,
  DollarSign,
  ScanLine,
  Camera,
  AlertTriangle,
  Radio,
  Lock,
  ShieldCheck,
  Eye,
  EyeOff,
  X,
  Smartphone,
} from 'lucide-react';

interface LoketPembayaranViewProps {
  siswaList: Siswa[];
  tagihanList: TagihanSiswa[];
  currentUser?: any;
  onProcessPayment: (payload: {
    siswaId: string;
    items: { tagihanId: string; nominal: number }[];
    channel: PaymentChannel;
    metodeBayar?: string;
    nominalBayar?: number;
    kasir?: string;
    keterangan?: string;
  }) => Promise<TransaksiPembayaran>;
  onShowReceipt: (trx: TransaksiPembayaran) => void;
}

export const LoketPembayaranView: React.FC<LoketPembayaranViewProps> = ({
  siswaList,
  tagihanList,
  currentUser,
  onProcessPayment,
  onShowReceipt,
}) => {
  const activeKasirName = currentUser?.nama || currentUser?.username || 'Petugas Loket';
  const { showAlert } = useDialog();
  // Default to first student
  const [selectedSiswaId, setSelectedSiswaId] = useState(siswaList.length > 0 ? siswaList[0].id : '');
  const [searchQuery, setSearchQuery] = useState('');
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [quickBarcodeInput, setQuickBarcodeInput] = useState('');
  const [barcodeAlert, setBarcodeAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [selectedTagihanIds, setSelectedTagihanIds] = useState<string[]>([]);
  const [customPayAmounts, setCustomPayAmounts] = useState<Record<string, number>>({});
  const [paymentChannel, setPaymentChannel] = useState<PaymentChannel>('CASH');
  const [cashReceived, setCashReceived] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [onlineGatewayUrl, setOnlineGatewayUrl] = useState<string | null>(null);
  const [activeQrisTransaction, setActiveQrisTransaction] = useState<TransaksiPembayaran | null>(null);

  // PIN Verification state for TABUNGAN payments
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinVisible, setPinVisible] = useState(false);
  const [pinAttempts, setPinAttempts] = useState(0);
  const [pinError, setPinError] = useState('');
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);
  const PIN_MAX_ATTEMPTS = 3;

  // VA Payment state
  const [showBankSelector, setShowBankSelector] = useState(false);
  const [activeVATransaction, setActiveVATransaction] = useState<(TransaksiPembayaran & { vaNumber?: string; expiredAt?: string; bankCode?: string }) | null>(null);
  const [isCreatingVA, setIsCreatingVA] = useState(false);

  // Quick Barcode / NIS / No. VA search handler
  const handleQuickBarcodeSearch = (code: string) => {
    const clean = code.trim();
    if (!clean) return;

    let matched = siswaList.find((s) => s.nis === clean);
    if (!matched) {
      matched = siswaList.find((s) => s.noVA === clean);
    }
    if (!matched) {
      matched = siswaList.find(
        (s) => clean.endsWith(s.nis) || (s.noVA && clean.includes(s.noVA))
      );
    }
    if (!matched) {
      matched = siswaList.find((s) => s.nama.toLowerCase().includes(clean.toLowerCase()));
    }

    if (matched) {
      setSelectedSiswaId(matched.id);
      setSelectedTagihanIds([]);
      setCustomPayAmounts({});
      setBarcodeAlert({
        type: 'success',
        message: `Siswa "${matched.nama}" (NIS: ${matched.nis}) berhasil dipilih via Barcode / QR!`,
      });
      setQuickBarcodeInput('');
      setTimeout(() => setBarcodeAlert(null), 3500);
    } else {
      setBarcodeAlert({
        type: 'error',
        message: `Kode "${clean}" tidak ditemukan. Pastikan barcode NIS / VA sesuai data siswa.`,
      });
      setTimeout(() => setBarcodeAlert(null), 4000);
    }
  };

  const selectedSiswa = useMemo(
    () => siswaList.find((s) => s.id === selectedSiswaId) || siswaList[0],
    [siswaList, selectedSiswaId]
  );

  const studentOptions = useMemo(() => {
    return siswaList.map((s) => ({
      value: s.id,
      label: `${s.nis} - ${s.nama} (${s.kelasNama})`,
      searchStr: `${s.nis} ${s.nama} ${s.kelasNama}`
    }));
  }, [siswaList]);

  // Unpaid bills for current student
  const studentBills = useMemo(() => {
    if (!selectedSiswa) return [];
    return tagihanList.filter((t) => t.siswaId === selectedSiswa.id && t.status !== 'PAID');
  }, [tagihanList, selectedSiswa]);

  // Toggle selection of a bill
  const toggleSelectBill = (id: string, sisa: number) => {
    setSelectedTagihanIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        const copy = { ...customPayAmounts };
        delete copy[id];
        setCustomPayAmounts(copy);
        return next;
      } else {
        setCustomPayAmounts((prevAmt) => ({ ...prevAmt, [id]: sisa }));
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTagihanIds.length === studentBills.length) {
      setSelectedTagihanIds([]);
      setCustomPayAmounts({});
    } else {
      const allIds = studentBills.map((b) => b.id);
      const amounts: Record<string, number> = {};
      studentBills.forEach((b) => {
        amounts[b.id] = b.sisa;
      });
      setSelectedTagihanIds(allIds);
      setCustomPayAmounts(amounts);
    }
  };

  const handleAmountChange = (billId: string, maxSisa: number, val: number) => {
    const safeVal = Math.min(Math.max(0, val), maxSisa);
    setCustomPayAmounts((prev) => ({ ...prev, [billId]: safeVal }));
  };

  // Calculate total payment
  const totalPayment = useMemo(() => {
    return selectedTagihanIds.reduce((sum, id) => sum + (customPayAmounts[id] || 0), 0);
  }, [selectedTagihanIds, customPayAmounts]);

  const isVAGateway = paymentChannel === 'DUITKU_VA';
  const isQrisChannel = paymentChannel === 'DUITKU_QRIS' || paymentChannel === 'QRIS';
  // Untuk metode QRIS, biaya layanan Rp 1.500 ditambahkan ke jumlah pembayaran (contoh: 50.000 + 1.500 = 51.500)
  const appFee = (isQrisChannel && totalPayment > 0) ? 1500 : 0; 
  const finalTotalPayment = totalPayment + appFee;

  // Aturan: QRIS hanya tampil jika totalPayment <= 50.000
  useEffect(() => {
    if (totalPayment > 50000 && (paymentChannel === 'DUITKU_QRIS' || paymentChannel === 'QRIS')) {
      setPaymentChannel('CASH');
    }
  }, [totalPayment, paymentChannel]);

  // Kembalian for CASH payments
  const kembalian = useMemo(() => {
    if (paymentChannel !== 'CASH') return 0;
    return Math.max(0, cashReceived - finalTotalPayment);
  }, [paymentChannel, cashReceived, finalTotalPayment]);

  const isCashShortage = paymentChannel === 'CASH' && cashReceived > 0 && cashReceived < finalTotalPayment;

  // Is saldo cukup for TABUNGAN & EWALLET?
  const isSaldoKurang = paymentChannel === 'TABUNGAN' && totalPayment > (selectedSiswa?.saldoTabungan || 0);
  const isSaldoEWalletKurang = paymentChannel === 'EWALLET' && totalPayment > (selectedSiswa?.saldoEWallet || 0);

  const handlePay = async () => {
    if (!selectedSiswa || selectedTagihanIds.length === 0 || totalPayment <= 0) {
      showAlert('Pilih setidaknya satu tagihan dan nominal pembayaran!');
      return;
    }

    if (paymentChannel === 'CASH') {
      if (cashReceived <= 0) {
        showAlert('Masukkan jumlah uang yang diterima dari siswa/wali terlebih dahulu!', { type: 'warning' });
        return;
      }
      if (cashReceived < finalTotalPayment) {
        showAlert(
          `Uang diterima (Rp ${cashReceived.toLocaleString('id-ID')}) kurang dari total tagihan (Rp ${finalTotalPayment.toLocaleString('id-ID')})!`,
          { type: 'danger', title: 'Uang Tidak Cukup' }
        );
        return;
      }
    }

    if (paymentChannel === 'TABUNGAN') {
      const isMinBalanceEnabled = localStorage.getItem('tabungan_minimumSaldo') === 'true';
      const minBalanceAmount = Number(localStorage.getItem('tabungan_minimumSaldoAmount')) || 10000;
      const isLimitEnabled = localStorage.getItem('tabungan_limitPenarikan') === 'true';
      const limitAmount = Number(localStorage.getItem('tabungan_limitPenarikanAmount')) || 100000;
      const currentSaldo = selectedSiswa.saldoTabungan || 0;

      if (isSaldoKurang) {
        showAlert(
          `Saldo tabungan siswa (Rp ${currentSaldo.toLocaleString('id-ID')}) tidak mencukupi untuk pembayaran sebesar Rp ${finalTotalPayment.toLocaleString('id-ID')}!`,
          { type: 'danger', title: 'Saldo Tidak Cukup' }
        );
        return;
      }

      if (isMinBalanceEnabled && (currentSaldo - finalTotalPayment < minBalanceAmount)) {
        showAlert(
          `Gagal! Sisa saldo mengendap di rekening tidak boleh kurang dari Rp ${minBalanceAmount.toLocaleString('id-ID')} (Minimum Saldo Aktif).`,
          { type: 'danger', title: 'Saldo Minimum' }
        );
        return;
      }

      if (isLimitEnabled && finalTotalPayment > limitAmount) {
        showAlert(
          `Gagal! Pembayaran dengan tabungan dibatasi maksimal Rp ${limitAmount.toLocaleString('id-ID')} / hari (Limit Penarikan Aktif).`,
          { type: 'danger', title: 'Limit Terlampaui' }
        );
        return;
      }
      // Open PIN verification modal instead of processing directly
      setPinInput('');
      setPinError('');
      setPinAttempts(0);
      setShowPinModal(true);
      return;
    }

    if (paymentChannel === 'EWALLET') {
      const currentSaldo = selectedSiswa.saldoEWallet || 0;

      if (isSaldoEWalletKurang) {
        showAlert(
          `Saldo e-wallet siswa (Rp ${currentSaldo.toLocaleString('id-ID')}) tidak mencukupi untuk pembayaran sebesar Rp ${finalTotalPayment.toLocaleString('id-ID')}!`,
          { type: 'danger', title: 'Saldo E-Wallet Tidak Cukup' }
        );
        return;
      }

      // Open PIN verification modal for E-Wallet security
      setPinInput('');
      setPinError('');
      setPinAttempts(0);
      setShowPinModal(true);
      return;
    }

    // Delegate all other channels (QRIS, CASH, DUITKU_VA, DUITKU_QRIS, MIDTRANS_SNAP) to handleExecutePayment
    await handleExecutePayment();
  };

  // Called after PIN is verified — actually process the payment
  const handleProcessAfterPinVerified = async () => {
    setShowPinModal(false);
    await handleExecutePayment();
  };

  // Core payment execution (separated so it can be called after PIN or directly)
  const handleExecutePayment = async () => {
    if (!selectedSiswa) return;

    // For DUITKU_VA: show bank selector instead of direct payment
    if (paymentChannel === 'DUITKU_VA') {
      setShowBankSelector(true);
      return;
    }

    try {
      setIsProcessing(true);
      const itemsToPay = selectedTagihanIds.map((id) => ({
        tagihanId: id,
        nominal: customPayAmounts[id] || 0,
      }));

      const responseAPI: any = await onProcessPayment({
        siswaId: selectedSiswa.id,
        items: itemsToPay,
        channel: paymentChannel,
        metodeBayar: isQrisChannel ? 'DUITKU_QRIS' : paymentChannel,
        kasir: activeKasirName,
        nominalBayar: finalTotalPayment,
        keterangan: paymentChannel === 'CASH'
          ? `Pembayaran Loket SDMT - CASH | Diterima: Rp ${cashReceived.toLocaleString('id-ID')} | Kembalian: Rp ${kembalian.toLocaleString('id-ID')}`
          : `Pembayaran Loket SDMT - ${paymentChannel}`,
      });

      // Robust fallback extraction: in case response is wrapped in 'transaction' or 'data'
      const trx = responseAPI.transaction || responseAPI.data?.transaction || responseAPI;

      if (!trx || !trx.noTransaksi) {
        throw new Error("Gagal membaca data transaksi dari server");
      }

      if (trx.paymentUrl) setOnlineGatewayUrl(trx.paymentUrl);

      if (paymentChannel === 'QRIS' || paymentChannel === 'DUITKU_QRIS') {
        setActiveQrisTransaction(trx);
      } else {
        onShowReceipt(trx);
        setSelectedTagihanIds([]);
        setCustomPayAmounts({});
        setCashReceived(0);
      }
    } catch (err: any) {
      showAlert(`Gagal memproses pembayaran: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle bank selection → create VA
  const handleSelectBank = async (bankCode: string) => {
    if (!selectedSiswa) return;
    try {
      setIsCreatingVA(true);
      const trx = await api.generateVAPayment({
        siswaId: selectedSiswa.id,
        tagihanIds: selectedTagihanIds,
        nominalBayar: totalPayment,
        bankCode,
        kasir: activeKasirName,
      });
      setShowBankSelector(false);
      setActiveVATransaction({ ...trx, bankCode });
    } catch (err: any) {
      showAlert(`Gagal membuat Virtual Account: ${err.message}`, { type: 'danger', title: 'Gagal' });
    } finally {
      setIsCreatingVA(false);
    }
  };

  // PIN verification handler
  const handleVerifyPin = async () => {
    if (!selectedSiswa || pinInput.length < 4) return;
    if (pinAttempts >= PIN_MAX_ATTEMPTS) return;

    try {
      setIsVerifyingPin(true);
      setPinError('');
      const result = await api.verifyStudentPin(selectedSiswa.id, pinInput);
      if (result.success) {
        await handleProcessAfterPinVerified();
      } else {
        const newAttempts = pinAttempts + 1;
        setPinAttempts(newAttempts);
        setPinInput('');
        if (newAttempts >= PIN_MAX_ATTEMPTS) {
          setPinError(`PIN salah 3x. Modal dikunci. Gunakan Reset PIN di menu Siswa.`);
        } else {
          setPinError(`${result.message} (${PIN_MAX_ATTEMPTS - newAttempts}x percobaan tersisa)`);
        }
      }
    } catch (err: any) {
      setPinError('Gagal menghubungi server. Coba lagi.');
    } finally {
      setIsVerifyingPin(false);
    }
  };

  const isQrisMethod = paymentChannel === 'QRIS' || paymentChannel === 'DUITKU_QRIS';

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Search & Student Bar (Document Page 10) */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <span>Loket Kasir Pembayaran Siswa (POS)</span>
              <span className="text-[10px] bg-sky-100 text-sky-800 font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <ScanLine className="w-3 h-3 text-sky-700" />
                <span>Barcode/QR Ready</span>
              </span>
              <span className="text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-0.5 rounded-full flex items-center gap-1.5 shadow-2xs">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>Petugas Kasir: <strong>{activeKasirName}</strong></span>
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Transaksi loket tunai, transfer perbankan, saldo tabungan, & integrasi gateway Duitku / Midtrans
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Quick Barcode / QR Scanner Input */}
            <div className="flex items-center gap-1.5 bg-slate-50 p-1.5 rounded-lg border border-sky-300">
              <div className="relative">
                <input
                  type="text"
                  value={quickBarcodeInput}
                  onChange={(e) => setQuickBarcodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleQuickBarcodeSearch(quickBarcodeInput);
                    }
                  }}
                  placeholder="Scan Barcode NIS / VA..."
                  className="w-48 sm:w-56 text-xs pl-7 pr-2 py-1.5 bg-white border border-slate-300 rounded font-mono font-bold focus:ring-1 focus:ring-sky-500 focus:outline-hidden"
                />
                <ScanLine className="w-3.5 h-3.5 text-sky-600 absolute left-2 top-2.5" />
              </div>
              <button
                type="button"
                onClick={() => handleQuickBarcodeSearch(quickBarcodeInput)}
                className="px-2.5 py-1.5 bg-sky-700 hover:bg-sky-800 text-white rounded text-xs font-bold transition flex items-center gap-1 cursor-pointer"
                title="Cari NIS / VA"
              >
                <Search className="w-3 h-3" />
                <span className="hidden sm:inline">Cari</span>
              </button>
            </div>

            {/* Camera / Modal Scanner Button */}
            <button
              type="button"
              onClick={() => setIsScannerOpen(true)}
              className="px-3 py-2 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 active:from-sky-800 text-white text-xs font-bold rounded-lg transition flex items-center gap-1.5 shadow-xs cursor-pointer"
              title="Buka Kamera / Scanner Barcode"
            >
              <QrCode className="w-4 h-4 text-amber-300" />
              <span>Pindai Barcode / QR</span>
            </button>

            {/* Dropdown Siswa fallback */}
            <div className="flex items-center gap-1.5 border-l border-slate-200 pl-2">
              <span className="text-xs font-semibold text-slate-500 hidden md:inline">Manual:</span>
              <SearchableSelect
                options={studentOptions}
                value={selectedSiswa?.id || ''}
                onChange={(val) => {
                  setSelectedSiswaId(val);
                  setSelectedTagihanIds([]);
                  setCustomPayAmounts({});
                  setCashReceived(0);
                  setPinInput('');
                  setPinError('');
                  setPinAttempts(0);
                  setShowPinModal(false);
                }}
                className="max-w-[200px] sm:max-w-[280px]"
              />
            </div>
          </div>
        </div>

        {/* Quick Barcode Scan Alert */}
        {barcodeAlert && (
          <div
            className={`p-2.5 rounded-lg text-xs font-semibold flex items-center gap-2 animate-in fade-in duration-150 ${
              barcodeAlert.type === 'success'
                ? 'bg-emerald-50 text-emerald-900 border border-emerald-300'
                : 'bg-rose-50 text-rose-900 border border-rose-300'
            }`}
          >
            {barcodeAlert.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span>{barcodeAlert.message}</span>
          </div>
        )}
      </div>

      {/* Student Badge Card (Matching Document Page 10 Top Header) */}
      {selectedSiswa && (
        <div className="bg-gradient-to-r from-sky-800 to-sky-700 rounded-xl p-5 text-white shadow-sm grid grid-cols-1 md:grid-cols-5 gap-3.5">
          <div>
            <span className="text-[11px] text-sky-200 uppercase font-semibold">Nama Siswa</span>
            <h3 className="text-base font-extrabold text-white mt-0.5">{selectedSiswa.nama}</h3>
            <p className="text-xs text-sky-200 font-mono mt-0.5">NIS: {selectedSiswa.nis}</p>
          </div>

          <div>
            <span className="text-[11px] text-sky-200 uppercase font-semibold">Kelas & Angkatan</span>
            <p className="text-sm font-bold text-white mt-0.5">{selectedSiswa.kelasNama}</p>
            <p className="text-xs text-sky-200 mt-0.5">Tahun Angkatan: {selectedSiswa.thnAngkatan}</p>
          </div>

          <div>
            <span className="text-[11px] text-sky-200 uppercase font-semibold">Nomor Virtual Account</span>
            <p className="text-sm font-mono font-bold text-amber-300 mt-0.5 tracking-wider">
              {selectedSiswa.noVA}
            </p>
            <p className="text-xs text-sky-200 mt-0.5">Bank Muamalat / Duitku / Midtrans</p>
          </div>

          <div className="bg-white/10 rounded-lg p-3 border border-white/15">
            <span className="text-[10px] text-sky-200 uppercase font-bold flex items-center gap-1">
              <Wallet className="w-3 h-3 text-emerald-400" />
              <span>Saldo Tabungan</span>
            </span>
            <p className="text-base font-mono font-extrabold text-emerald-300 mt-0.5">
              Rp {(selectedSiswa.saldoTabungan || 0).toLocaleString('id-ID')}
            </p>
            <p className="text-[10px] text-sky-200">Dapat dipakai bayar</p>
          </div>

          <div className="bg-indigo-950/40 rounded-lg p-3 border border-indigo-400/30">
            <span className="text-[10px] text-indigo-200 uppercase font-bold flex items-center gap-1">
              <Smartphone className="w-3 h-3 text-indigo-300" />
              <span>Saldo E-Wallet</span>
            </span>
            <p className="text-base font-mono font-extrabold text-indigo-200 mt-0.5">
              Rp {(selectedSiswa.saldoEWallet || 0).toLocaleString('id-ID')}
            </p>
            <p className="text-[10px] text-indigo-200/80">Real-time • Siap potong</p>
          </div>

          {/* Attendance & RFID Terminal Status Badge */}
          {selectedSiswa.kehadiran && (
            <div className="md:col-span-5 bg-sky-950/40 rounded-lg px-3.5 py-2.5 border border-sky-600/40 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <Radio className="w-4 h-4 text-amber-400 animate-pulse" />
                <div>
                  <span className="text-sky-200 text-[11px]">Presensi Mesin RFID Solution X107: </span>
                  <strong className="text-white font-mono">{selectedSiswa.kehadiran.persentaseHadir}%</strong>
                  <span className="text-sky-300 text-[11px] ml-1.5 font-mono">
                    ({selectedSiswa.kehadiran.hadir}/{selectedSiswa.kehadiran.totalHariEfektif} hari aktif)
                  </span>
                  {selectedSiswa.rfidCard && (
                    <span className="text-slate-300 text-[10px] ml-2 font-mono bg-sky-900/80 px-1.5 py-0.5 rounded border border-sky-700">
                      UID: {selectedSiswa.rfidCard}
                    </span>
                  )}
                </div>
              </div>

              {selectedSiswa.kehadiran.persentaseHadir < 80 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-500/30 border border-rose-400 text-rose-100 rounded-lg text-[11px] font-bold">
                  <AlertTriangle className="w-3.5 h-3.5 text-rose-300" />
                  <span>Peringatan: Tingkat kehadiran rendah ({selectedSiswa.kehadiran.persentaseHadir}%)</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Billing Table & Payment Summary (Matching Document Page 10 & 11) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left 2 Cols: Tagihan List */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="select-all"
                  checked={
                    studentBills.length > 0 && selectedTagihanIds.length === studentBills.length
                  }
                  onChange={handleSelectAll}
                  className="rounded text-sky-600 focus:ring-sky-500 w-4 h-4 cursor-pointer"
                />
                <label htmlFor="select-all" className="text-xs font-bold text-slate-800 cursor-pointer">
                  Pilih Semua Tagihan Belum Lunas ({studentBills.length})
                </label>
              </div>

              <span className="text-xs text-slate-500">
                {selectedTagihanIds.length} item tagihan dipilih
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="py-2.5 px-3 w-10 text-center">Pilih</th>
                    <th className="py-2.5 px-3">Pos & Deskripsi</th>
                    <th className="py-2.5 px-3 text-right">Tarif</th>
                    <th className="py-2.5 px-3 text-right">Sisa Tagihan</th>
                    <th className="py-2.5 px-3 text-right w-36">Bayar Sekarang (Rp)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {studentBills.map((b) => {
                    const isSelected = selectedTagihanIds.includes(b.id);
                    return (
                      <tr
                        key={b.id}
                        className={`transition ${isSelected ? 'bg-sky-50/70' : 'hover:bg-slate-50'}`}
                      >
                        <td className="py-2.5 px-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectBill(b.id, b.sisa)}
                            className="rounded text-sky-600 focus:ring-sky-500 w-4 h-4 cursor-pointer"
                          />
                        </td>
                        <td className="py-2.5 px-3">
                          <p className="font-bold text-slate-900">
                            {b.namaPos} {b.bulan && b.bulan !== '-' ? `- ${b.bulan}` : ''}
                          </p>
                          <p className="text-[11px] text-slate-500 font-mono">
                            T.A: {b.tahunAjaran} | Kode Pos: {b.kodePos}
                          </p>
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-600">
                          {b.total.toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-bold text-rose-600">
                          {b.sisa.toLocaleString('id-ID')}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <input
                            type="number"
                            disabled={!isSelected}
                            value={customPayAmounts[b.id] ?? ''}
                            onChange={(e) =>
                              handleAmountChange(b.id, b.sisa, Number(e.target.value))
                            }
                            className={`w-full text-right px-2 py-1 font-mono font-bold border rounded text-xs focus:ring-1 focus:ring-sky-500 ${
                              isSelected
                                ? 'bg-white border-sky-400 text-sky-900'
                                : 'bg-slate-100 text-slate-400 border-slate-200'
                            }`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {studentBills.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-slate-400">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2 opacity-80" />
                        Semua tagihan siswa ini telah LUNAS!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Col: Payment Method & Execution Panel (Document Page 11) */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
            <h3 className="font-bold text-sm text-slate-900 border-b pb-2 flex items-center justify-between">
              <span>Metode Pembayaran</span>
              <span className="text-[11px] text-sky-700 bg-sky-50 px-2 py-0.5 rounded font-semibold">
                Multi-Channel
              </span>
            </h3>

            {/* Channels List */}
            <div className="space-y-2">
              {/* QRIS Channel (Hidden per user request) */}              <label
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${
                  paymentChannel === 'CASH'
                    ? 'border-sky-500 bg-sky-50/70 text-sky-950 font-semibold'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="channel"
                  checked={paymentChannel === 'CASH'}
                  onChange={() => setPaymentChannel('CASH')}
                  className="text-sky-600 focus:ring-sky-500"
                />
                <Banknote className="w-4 h-4 text-emerald-600" />
                <div className="text-xs">
                  <p className="font-bold">Tunai di Kasir Loket</p>
                  <p className="text-[10px] text-slate-500">Penerimaan kas tunai di loket TU SDMT</p>
                </div>
              </label>

              <label
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${
                  paymentChannel === 'TABUNGAN'
                    ? 'border-sky-500 bg-sky-50/70 text-sky-950 font-semibold'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="channel"
                  checked={paymentChannel === 'TABUNGAN'}
                  onChange={() => setPaymentChannel('TABUNGAN')}
                  className="text-sky-600 focus:ring-sky-500"
                />
                <Wallet className="w-4 h-4 text-sky-600" />
                <div className="text-xs flex-1">
                  <p className="font-bold">Potong Saldo Tabungan Siswa</p>
                  <p className={`text-[10px] font-semibold ${
                    isSaldoKurang ? 'text-rose-600' : 'text-emerald-600'
                  }`}>
                    Saldo: Rp {(selectedSiswa?.saldoTabungan || 0).toLocaleString('id-ID')}
                    {isSaldoKurang && ' ⚠ Tidak Cukup'}
                  </p>
                </div>
                {paymentChannel === 'TABUNGAN' && (
                  <Lock className="w-3.5 h-3.5 text-sky-500 shrink-0" aria-label="Butuh verifikasi PIN" />
                )}
              </label>

              <label
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${
                  paymentChannel === 'EWALLET'
                    ? 'border-indigo-500 bg-indigo-50/70 text-indigo-950 font-semibold ring-1 ring-indigo-400'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="channel"
                  checked={paymentChannel === 'EWALLET'}
                  onChange={() => setPaymentChannel('EWALLET')}
                  className="text-indigo-600 focus:ring-indigo-500"
                />
                <Smartphone className="w-4 h-4 text-indigo-600" />
                <div className="text-xs flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-bold">Potong Saldo E-Wallet Siswa (NEW)</p>
                    <span className="text-[9px] bg-indigo-100 text-indigo-800 font-extrabold px-1.5 py-0.5 rounded">
                      Otomatis Debit
                    </span>
                  </div>
                  <p className={`text-[10px] font-semibold ${
                    isSaldoEWalletKurang ? 'text-rose-600' : 'text-emerald-600'
                  }`}>
                    Saldo: Rp {(selectedSiswa?.saldoEWallet || 0).toLocaleString('id-ID')}
                    {isSaldoEWalletKurang && ' ⚠ Tidak Cukup (Pembayaran Diblokir)'}
                  </p>
                </div>
                {paymentChannel === 'EWALLET' && (
                  <Lock className="w-3.5 h-3.5 text-indigo-500 shrink-0" aria-label="Butuh verifikasi PIN" />
                )}
              </label>

              <label
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${
                  paymentChannel === 'DUITKU_VA'
                    ? 'border-amber-500 bg-amber-50/70 text-amber-950 font-semibold ring-1 ring-amber-400'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="channel"
                  checked={paymentChannel === 'DUITKU_VA'}
                  onChange={() => setPaymentChannel('DUITKU_VA')}
                  className="text-amber-600 focus:ring-amber-500"
                />
                <CreditCard className="w-4 h-4 text-amber-600 shrink-0" />
                <div className="text-xs flex-1">
                  <p className="font-bold">Virtual Account (VA BCA, VA BRI, VA Mandiri)</p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {VA_BANK_LIST.map(b => (
                      <span key={b.code} className="text-[9px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {b.bank}
                      </span>
                    ))}
                  </div>
                </div>
                {paymentChannel === 'DUITKU_VA' && (
                  <span className="text-[9px] bg-amber-100 text-amber-800 font-bold px-1.5 py-0.5 rounded shrink-0">Pilih Bank</span>
                )}
              </label>

              {/* QRIS hanya tampil jika total tagihan <= 50.000 */}
              {totalPayment <= 50000 && (
                <label
                  className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${
                    paymentChannel === 'DUITKU_QRIS'
                      ? 'border-sky-500 bg-sky-50/70 text-sky-950 font-semibold'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="channel"
                    checked={paymentChannel === 'DUITKU_QRIS'}
                    onChange={() => setPaymentChannel('DUITKU_QRIS')}
                    className="text-sky-600 focus:ring-sky-500"
                  />
                  <QrCode className="w-4 h-4 text-purple-600" />
                  <div className="text-xs">
                    <p className="font-bold">QRIS Standar Nasional</p>
                    <p className="text-[10px] text-slate-500">Maks. Rp 50.000 • Biaya: Rp 1.500</p>
                  </div>
                </label>
              )}

              {/* MIDTRANS SNAP Channel (Hidden per user request) */}
            </div>

            {/* Payment Summary Calculation */}
            <div className="bg-slate-50 p-3.5 rounded-lg border border-slate-200 space-y-2 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>Jumlah Tagihan:</span>
                <span className="font-bold">{selectedTagihanIds.length} Item</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Total Tagihan:</span>
                <span className="font-mono font-semibold">
                  Rp {totalPayment.toLocaleString('id-ID')}
                </span>
              </div>
              {appFee > 0 && (
                <div className="flex justify-between text-sky-700 font-semibold">
                  <span>Biaya Layanan & Bank (QRIS):</span>
                  <span className="font-mono font-bold">
                    +Rp {appFee.toLocaleString('id-ID')}
                  </span>
                </div>
              )}
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                <span className="font-bold text-slate-800 text-sm">TOTAL BAYAR:</span>
                <span className="font-mono text-lg font-black text-sky-900">
                  Rp {finalTotalPayment.toLocaleString('id-ID')}
                </span>
              </div>

              {/* ===== E-WALLET PAYMENT SECTION ===== */}
              {paymentChannel === 'EWALLET' && (
                <div className="border-t border-indigo-200 pt-3 space-y-2.5">
                  <div className="p-3 bg-indigo-50/80 border border-indigo-200 rounded-lg text-xs space-y-1.5">
                    <div className="flex justify-between items-center text-indigo-950">
                      <span className="font-semibold flex items-center gap-1.5">
                        <Smartphone className="w-3.5 h-3.5 text-indigo-600" />
                        Saldo Real-Time E-Wallet:
                      </span>
                      <span className="font-mono font-bold text-sm text-indigo-700">
                        Rp {(selectedSiswa?.saldoEWallet || 0).toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-slate-600 text-[11px]">
                      <span>Nominal Tagihan Didebit:</span>
                      <span className="font-mono font-bold text-rose-600">
                        -Rp {totalPayment.toLocaleString('id-ID')}
                      </span>
                    </div>
                    <div className="border-t border-indigo-100 pt-1 flex justify-between items-center text-xs">
                      <span className="font-semibold text-slate-700">Sisa Saldo Setelah Debit:</span>
                      <span className={`font-mono font-bold ${
                        isSaldoEWalletKurang ? 'text-rose-600 font-black' : 'text-emerald-700'
                      }`}>
                        Rp {Math.max(0, (selectedSiswa?.saldoEWallet || 0) - totalPayment).toLocaleString('id-ID')}
                      </span>
                    </div>
                  </div>

                  {isSaldoEWalletKurang ? (
                    <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-[11px] text-rose-700 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <div>
                        <strong className="block font-bold">Peringatan: Saldo Kurang! Pembayaran Diblokir.</strong>
                        <span>
                          Saldo E-Wallet siswa saat ini <strong>Rp {(selectedSiswa?.saldoEWallet || 0).toLocaleString('id-ID')}</strong> tidak mencukupi untuk tagihan <strong>Rp {totalPayment.toLocaleString('id-ID')}</strong> (Kurang Rp {(totalPayment - (selectedSiswa?.saldoEWallet || 0)).toLocaleString('id-ID')}). Sistem memblokir pembayaran agar saldo tidak minus.
                        </span>
                      </div>
                    </div>
                  ) : totalPayment > 0 && (
                    <p className="text-[11px] text-indigo-700 font-medium flex items-center gap-1.5 bg-indigo-50/50 p-2 rounded border border-indigo-100">
                      <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                      Saldo mencukupi. Dilengkapi otorisasi verifikasi PIN kasir untuk keamanan loket.
                    </p>
                  )}
                </div>
              )}

              {/* ===== CASH PAYMENT SECTION ===== */}
              {paymentChannel === 'CASH' && (
                <div className="border-t border-emerald-200 pt-3 space-y-2.5">
                  {/* Uang Diterima Input */}
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
                      <Banknote className="w-3.5 h-3.5 text-emerald-600" />
                      Uang Diterima dari Wali/Siswa
                    </label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500 pointer-events-none">
                        Rp
                      </span>
                      <input
                        id="input-cash-received"
                        type="number"
                        min={0}
                        step={1000}
                        value={cashReceived === 0 ? '' : cashReceived}
                        onChange={(e) => setCashReceived(Number(e.target.value) || 0)}
                        placeholder={totalPayment > 0 ? totalPayment.toString() : '0'}
                        className={`w-full text-right pl-8 pr-2.5 py-2 font-mono font-bold border rounded-lg text-sm focus:ring-2 focus:outline-hidden transition ${
                          isCashShortage
                            ? 'border-rose-400 bg-rose-50 text-rose-700 focus:ring-rose-400'
                            : cashReceived >= totalPayment && totalPayment > 0
                            ? 'border-emerald-400 bg-emerald-50 text-emerald-800 focus:ring-emerald-400'
                            : 'border-slate-300 bg-white text-slate-800 focus:ring-sky-400'
                        }`}
                      />
                    </div>
                    {/* Quick amount buttons */}
                    {totalPayment > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {[totalPayment, ...[
                          Math.ceil(totalPayment / 5000) * 5000,
                          Math.ceil(totalPayment / 10000) * 10000,
                          Math.ceil(totalPayment / 50000) * 50000,
                          Math.ceil(totalPayment / 100000) * 100000,
                        ].filter(v => v > totalPayment)].slice(0, 4).map((amt, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setCashReceived(amt)}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold border transition cursor-pointer ${
                              cashReceived === amt
                                ? 'bg-emerald-600 text-white border-emerald-600'
                                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {amt === totalPayment ? 'Pas' : `Rp ${(amt / 1000).toFixed(0)}rb`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Kurang / Kembalian */}
                  {cashReceived > 0 && (
                    <div className={`rounded-lg p-2.5 flex justify-between items-center border ${
                      isCashShortage
                        ? 'bg-rose-50 border-rose-300'
                        : 'bg-emerald-50 border-emerald-300'
                    }`}>
                      <span className={`font-bold text-sm ${
                        isCashShortage ? 'text-rose-700' : 'text-emerald-800'
                      }`}>
                        {isCashShortage ? '⚠ KURANG:' : '✓ KEMBALIAN:'}
                      </span>
                      <span className={`font-mono text-base font-black ${
                        isCashShortage ? 'text-rose-700' : 'text-emerald-700'
                      }`}>
                        {isCashShortage
                          ? `- Rp ${(finalTotalPayment - cashReceived).toLocaleString('id-ID')}`
                          : `Rp ${kembalian.toLocaleString('id-ID')}`
                        }
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Execute Payment Button */}
            <button
              id="btn-process-payment"
              disabled={
                isProcessing ||
                selectedTagihanIds.length === 0 ||
                totalPayment === 0 ||
                (paymentChannel === 'CASH' && (cashReceived <= 0 || isCashShortage)) ||
                (paymentChannel === 'TABUNGAN' && isSaldoKurang) ||
                (paymentChannel === 'EWALLET' && isSaldoEWalletKurang)
              }
              onClick={handlePay}
              className={`w-full py-3 rounded-lg font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition ${
                selectedTagihanIds.length > 0 &&
                totalPayment > 0 &&
                !isProcessing &&
                !(paymentChannel === 'CASH' && (cashReceived <= 0 || isCashShortage)) &&
                !(paymentChannel === 'TABUNGAN' && isSaldoKurang) &&
                !(paymentChannel === 'EWALLET' && isSaldoEWalletKurang)
                  ? isQrisMethod
                    ? 'bg-rose-600 hover:bg-rose-500 text-white cursor-pointer shadow-md'
                    : paymentChannel === 'EWALLET'
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer shadow-md'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isQrisMethod ? (
                <>
                  <QrCode className="w-4 h-4" />
                  <span>
                    {isProcessing ? 'Menyiapkan QRIS...' : `Tampilkan QRIS & Scan Bayar (Rp ${finalTotalPayment.toLocaleString('id-ID')})`}
                  </span>
                </>
              ) : paymentChannel === 'CASH' ? (
                <>
                  <Banknote className="w-4 h-4" />
                  <span>
                    {isProcessing
                      ? 'Memproses Transaksi...'
                      : cashReceived <= 0
                      ? 'Masukkan Uang Diterima'
                      : isCashShortage
                      ? 'Uang Kurang'
                      : `Konfirmasi Bayar & Cetak Kuitansi`}
                  </span>
                </>
              ) : paymentChannel === 'TABUNGAN' ? (
                <>
                  <Lock className="w-4 h-4" />
                  <span>
                    {isProcessing
                      ? 'Memproses...'
                      : isSaldoKurang
                      ? 'Saldo Tidak Mencukupi'
                      : 'Verifikasi PIN & Bayar Tabungan'}
                  </span>
                </>
              ) : paymentChannel === 'EWALLET' ? (
                <>
                  <Lock className="w-4 h-4" />
                  <span>
                    {isProcessing
                      ? 'Memproses...'
                      : isSaldoEWalletKurang
                      ? `Saldo E-Wallet Tidak Cukup (Kurang Rp ${(totalPayment - (selectedSiswa?.saldoEWallet || 0)).toLocaleString('id-ID')})`
                      : 'Verifikasi PIN Kasir & Potong E-Wallet'}
                  </span>
                </>
              ) : paymentChannel === 'DUITKU_VA' ? (
                <>
                  <CreditCard className="w-4 h-4" />
                  <span>{isProcessing ? 'Menyiapkan...' : 'Pilih Bank & Buat Virtual Account'}</span>
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4" />
                  <span>
                    {isProcessing ? 'Memproses Transaksi...' : 'Bayar Sekarang & Cetak Kuitansi'}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* QRIS Interactive Payment Modal */}
      {activeQrisTransaction && (
        <QrisPaymentModal
          transaction={activeQrisTransaction}
          onClose={() => setActiveQrisTransaction(null)}
          onPaymentSuccess={(settledTrx) => {
            setActiveQrisTransaction(null);
            onShowReceipt(settledTrx);
            setSelectedTagihanIds([]);
            setCustomPayAmounts({});
          }}
        />
      )}

      {/* Barcode / QR Code Quick Student Scanner Modal */}
      <BarcodeScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        siswaList={siswaList}
        onSelectSiswa={(siswa) => {
          setSelectedSiswaId(siswa.id);
          setSelectedTagihanIds([]);
          setCustomPayAmounts({});
          setBarcodeAlert({
            type: 'success',
            message: `Siswa "${siswa.nama}" (NIS: ${siswa.nis}) berhasil dipilih via Scanner Barcode!`,
          });
          setTimeout(() => setBarcodeAlert(null), 3500);
        }}
      />

      {/* ===== PIN VERIFICATION MODAL (TABUNGAN & EWALLET) ===== */}
      {showPinModal && selectedSiswa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className={`p-5 text-white relative ${
              paymentChannel === 'EWALLET'
                ? 'bg-gradient-to-r from-indigo-800 to-indigo-700'
                : 'bg-gradient-to-r from-sky-700 to-sky-600'
            }`}>
              <button
                onClick={() => { setShowPinModal(false); setPinInput(''); setPinError(''); }}
                className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-lg transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base">
                    {paymentChannel === 'EWALLET' ? 'Otorisasi PIN Kasir / Siswa' : 'Verifikasi PIN Siswa'}
                  </h3>
                  <p className="text-white/80 text-[11px]">
                    {paymentChannel === 'EWALLET'
                      ? 'Keamanan otorisasi loket potong saldo E-Wallet'
                      : 'Wajib untuk pembayaran via Tabungan'}
                  </p>
                </div>
              </div>
            </div>

            {/* Siswa Info + Saldo */}
            <div className={`border-b px-5 py-3 flex items-center justify-between ${
              paymentChannel === 'EWALLET' ? 'bg-indigo-50 border-indigo-100' : 'bg-sky-50 border-sky-100'
            }`}>
              <div>
                <p className="text-xs font-bold text-slate-900">{selectedSiswa.nama}</p>
                <p className="text-[10px] text-slate-500 font-mono">NIS: {selectedSiswa.nis} | {selectedSiswa.kelasNama}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-slate-500">
                  {paymentChannel === 'EWALLET' ? 'Saldo E-Wallet Siswa' : 'Saldo Tabungan'}
                </p>
                <p className={`text-sm font-extrabold font-mono ${
                  paymentChannel === 'EWALLET' ? 'text-indigo-700' : 'text-emerald-700'
                }`}>
                  Rp {(paymentChannel === 'EWALLET' ? (selectedSiswa.saldoEWallet || 0) : (selectedSiswa.saldoTabungan || 0)).toLocaleString('id-ID')}
                </p>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="px-5 pt-4 pb-2">
              <div className="bg-slate-50 rounded-lg p-3 flex justify-between items-center border border-slate-200 mb-4">
                <span className="text-xs font-semibold text-slate-600">Total Tagihan Dibayar:</span>
                <span className="font-mono text-base font-black text-sky-900">
                  Rp {totalPayment.toLocaleString('id-ID')}
                </span>
              </div>

              {/* PIN Input */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-slate-500" />
                  <span>
                    {paymentChannel === 'EWALLET' ? 'Masukkan PIN Kasir atau PIN Siswa' : 'Masukkan PIN 6 Digit Siswa'}
                  </span>
                  {pinAttempts > 0 && (
                    <span className={`ml-auto font-bold text-[10px] px-2 py-0.5 rounded-full ${
                      pinAttempts >= PIN_MAX_ATTEMPTS
                        ? 'bg-rose-100 text-rose-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {pinAttempts >= PIN_MAX_ATTEMPTS
                        ? 'TERKUNCI'
                        : `${PIN_MAX_ATTEMPTS - pinAttempts}x tersisa`}
                    </span>
                  )}
                </label>

                <div className="relative">
                  <input
                    id="input-pin-tabungan"
                    type={pinVisible ? 'text' : 'password'}
                    maxLength={10}
                    value={pinInput}
                    disabled={pinAttempts >= PIN_MAX_ATTEMPTS || isVerifyingPin}
                    onChange={(e) => {
                      setPinInput(e.target.value.slice(0, 10));
                      setPinError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && pinInput.length >= 4 && pinAttempts < PIN_MAX_ATTEMPTS) {
                        handleVerifyPin();
                      }
                    }}
                    autoFocus
                    placeholder="••••••"
                    className={`w-full text-center tracking-[0.5em] text-xl font-mono font-black px-4 py-3 border-2 rounded-xl focus:outline-hidden focus:ring-2 transition ${
                      pinAttempts >= PIN_MAX_ATTEMPTS
                        ? 'bg-rose-50 border-rose-300 text-rose-400 cursor-not-allowed'
                        : pinError
                        ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
                        : pinInput.length >= 4
                        ? 'border-emerald-400 bg-emerald-50 focus:ring-emerald-300'
                        : 'border-slate-300 bg-white focus:ring-sky-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setPinVisible(!pinVisible)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {pinVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* PIN dots indicator */}
                <div className="flex justify-center gap-2 mt-1">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all duration-150 ${
                        i < pinInput.length
                          ? pinAttempts >= PIN_MAX_ATTEMPTS
                            ? 'bg-rose-400'
                            : paymentChannel === 'EWALLET'
                            ? 'bg-indigo-600 scale-110'
                            : 'bg-sky-500 scale-110'
                          : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>

                {/* Error message */}
                {pinError && (
                  <div className="flex items-center gap-2 text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 animate-in fade-in duration-150">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    {pinError}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="px-5 pb-5 pt-3 flex gap-2">
              <button
                type="button"
                onClick={() => { setShowPinModal(false); setPinInput(''); setPinError(''); }}
                className="flex-1 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50 transition cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                id="btn-confirm-pin"
                disabled={pinInput.length < 4 || isVerifyingPin || pinAttempts >= PIN_MAX_ATTEMPTS}
                onClick={handleVerifyPin}
                className={`flex-2 flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition ${
                  pinInput.length >= 4 && !isVerifyingPin && pinAttempts < PIN_MAX_ATTEMPTS
                    ? paymentChannel === 'EWALLET'
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer shadow-sm'
                      : 'bg-sky-600 hover:bg-sky-700 text-white cursor-pointer shadow-sm'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isVerifyingPin ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Memverifikasi...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    <span>
                      {paymentChannel === 'EWALLET' ? 'Otorisasi & Potong E-Wallet' : 'Verifikasi & Bayar'}
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* Hint */}
            <div className="bg-slate-50 border-t border-slate-100 px-5 py-2.5 text-[10px] text-slate-500 text-center leading-relaxed">
              {paymentChannel === 'EWALLET'
                ? 'Gunakan PIN/Password akun Kasir bertugas atau PIN Siswa untuk otorisasi.'
                : <>PIN default siswa baru: <strong className="font-mono text-slate-600">123456</strong> · Reset di menu <strong>Data Siswa → Reset PIN</strong></>}
            </div>
          </div>
        </div>
      )}

      {/* ===== BANK SELECTOR MODAL (Pilih Bank VA) ===== */}
      {showBankSelector && selectedSiswa && (
        <BankSelectorModal
          totalPayment={finalTotalPayment}
          siswaName={selectedSiswa.nama}
          onSelectBank={handleSelectBank}
          onClose={() => setShowBankSelector(false)}
          isLoading={isCreatingVA}
        />
      )}

      {/* ===== VA PAYMENT MODAL (Tampilkan Nomor VA + Instruksi) ===== */}
      {activeVATransaction && (
        <VAPaymentModal
          transaction={activeVATransaction}
          onClose={() => {
            setActiveVATransaction(null);
            setSelectedTagihanIds([]);
            setCustomPayAmounts({});
          }}
          onPaymentSuccess={(settledTrx) => {
            setActiveVATransaction(null);
            onShowReceipt(settledTrx);
            setSelectedTagihanIds([]);
            setCustomPayAmounts({});
          }}
        />
      )}
    </div>
  );
};
